import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { PromoPageKeys } from 'sections/PromoSection/pages/PromoPage/types'
import { ButtonPromo, TextPromo } from 'components/ui'
import { Href } from 'components/ui/Href/Href'
import { runAdriverTracking } from 'utils/adriver/runAdriverTracking'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { URLS } from 'shared/constants/urls'
import { Country } from 'shared/types/types'

import { cookieService } from './api'
import { ILayoutModel } from './layoutModel'

import s from './CookiePolicy.module.scss'

export const COOKIE_POLICY_NAME = '_cp_v3'
const CHANNEL_NAME = '_cookiePolicy'
const MILLISECONDS_IN_A_DAY = 86400000

export const CookiePolicy = observer(({ layoutModel }: { layoutModel: ILayoutModel }) => {
  const { appStore } = useStore()
  const [isVisible, setIsVisible] = useState(false)

  const { t } = useTranslation()

  const getKey = (key: string) => `${PromoPageKeys.cookiePolicy}.${key}`
  let channel: BroadcastChannel | null = null
  const privatePolicyUrl = isGeDomain() ? URLS.wbMarketPrivatePolicyGe : URLS.wbMarketPrivatePolicy

  useEffect(() => {
    const checkCookiePolicy = () => {
      const cookieExists = getCookie(COOKIE_POLICY_NAME) === '1'
      if (cookieExists) {
        loadAnalyticsScript()
        runAdriverTracking()
        hideBanner()
      } else {
        showBanner()
        checkCountry()
      }
    }
    checkCookiePolicy()
  }, [])

  const checkCountry = async () => {
    const response = await cookieService.getCountryByIp()
    if (response.countryCode === Country.RU) {
      loadAnalyticsScript()
    }
  }

  const showBanner = () => {
    setIsVisible(true)
    layoutModel.analytic.sendEvent('Alert_S', { name: 'cookies_alert' })

    if (window.BroadcastChannel) {
      channel = new BroadcastChannel(CHANNEL_NAME)
      channel.addEventListener('message', hideBanner)
    }
  }

  const hideBanner = () => {
    setIsVisible(false)
    if (channel) {
      channel.close()
      channel = null
    }
    layoutModel.eventBus.dispatchEvent(new CustomEvent('CookiePolicyHide'))
  }

  const loadAnalyticsScript = (): void => {
    ;(function (w: Window & { [key: string]: any }, d: Document, s: string, c: string, t: string) {
      w[t] =
        w[t] ||
        function (...args: any[]) {
          ;(w[t].q = w[t].q || []).push(args)
        }
      w[t].l = Date.now()
      const a: HTMLScriptElement = d.createElement(s) as HTMLScriptElement
      const m: HTMLElement | null = d.getElementsByTagName(s)[0] as HTMLElement
      a.async = true
      a.src = c
      if (m?.parentNode) {
        m.parentNode.insertBefore(a, m)
      }
    })(window, document, 'script', 'https://a.wb.ru/sdk/sdk.js', 'wba')

    if (window.wba) {
      window.wba('init', { counterId: 58, clientId: appStore.user?.info.user_id || '' })
    }
  }

  const handleOkClick = () => {
    if (getCookie(COOKIE_POLICY_NAME) !== '1') {
      setCookie(COOKIE_POLICY_NAME, '1', 90)
      layoutModel.analytic.sendEvent('Alert_T', { name: 'cookies_alert', type: 'ok' })
    }
    if (channel) {
      channel.postMessage('hide')
    }
    loadAnalyticsScript()
    runAdriverTracking()
    hideBanner()
  }

  const getCookie = (name: string): string | null => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    if (match) return match[2]
    return null
  }

  const setCookie = (name: string, value: string, days: number): void => {
    const expires = new Date(Date.now() + days * MILLISECONDS_IN_A_DAY).toUTCString()
    document.cookie = name + '=' + value + '; expires=' + expires + '; path=/'
  }

  return (
    isVisible && (
      <div className={s.content}>
        <div className={s.wrapper}>
          <TextPromo className={s.text} size="s12" fontFamily="ASLHauss">
            {t(getKey('Мы используем'))}{' '}
            <Href href={privatePolicyUrl} className={s.link} target="_blank" linkType="search">
              {t(getKey('cookies'))}
            </Href>
            , {t(getKey('чтобы собирать статистику'))}
          </TextPromo>
          <ButtonPromo style={{ height: 40, marginLeft: 'auto' }} onClick={handleOkClick}>
            {t(getKey('Хорошо'))}
          </ButtonPromo>
        </div>
      </div>
    )
  )
})
